import {
  Box,
  Button,
  CircularProgress,
  ListItemText,
  Menu,
  MenuItem,
  Modal,
  TextField,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';

// Hooks
import { useDeleteTranscript } from '@hooks/deleteQueries';
import {
  useFetchAssignedTags,
  useFetchStudyTags,
  useFetchTranscriptList,
  useSingleInterview,
} from '@hooks/getQueries';
import { useAddStudyTag, useMarkAsCompleted, useUpdateTranscriptName } from '@hooks/postQueries';

// Components
import RenameTranscriptModal from './components/RenameTranscriptModal';
import TagLibraryModal from './components/TagLibraryModal';
import TranscriptItem from './components/TranscriptItem';
import TranscriptListHeader from './components/TranscriptListHeader';

// Utilities and hooks
import ConfirmationDialog from '@components/common/ConfirmationDialog';
import DeleteConfirmationDialog from '@components/DeleteConfirmation';
import AssignTags from '@components/interview/Tag/AssignTags';
import Spinner from '@components/spinner/Spinner';
import usePermission from '@hooks/usePermission';
import toast from 'react-hot-toast';
import FileUpload from '../upload/FileUpload';
import { useInfiniteScroll } from './hooks/useInfiniteScroll';

const useStyles = makeStyles(() => ({
  sidebarContainer: {
    height: 'calc(100vh - 135px)',
    display: 'flex',
    flexDirection: 'column',
  },
  stickyHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e8e8e8',
  },
  fieldList: {
    flex: 1,
    padding: '0',
    overflow: 'auto',
    '& > div': {
      borderRadius: '5px',
    },
  },
}));

const TranscriptList = () => {
  const classes = useStyles();
  const { transcriptId, id } = useParams();
  const navigate = useNavigate();
  const [initialLoad, setInitialLoad] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState(null);
  const [headerAnchorEl, setHeaderAnchorEl] = useState(null);
  const [currentTranscript, setCurrentTranscript] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteTranscriptText, setDeleteTranscriptText] = useState('');
  const [openRenameTranscriptModal, setOpenRenameTranscriptModal] = useState(false);
  const [transcriptIdState, setTranscriptIdState] = useState(null);
  const editTranscriptNameRef = useRef(null);
  const [openTagModal, setOpenTagModal] = useState(false);
  const [openTagLibraryModal, setOpenTagLibraryModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchQueryAssignedTag] = useState('');
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [open, setOpen] = useState(false);
  const { mutate: markAsCompleted, isPending: isMarkingAsCompletedPending } = useMarkAsCompleted();
  const [selectedTags, setSelectedTags] = useState(() => {
    try {
      const savedTags = localStorage.getItem(`transcript-filter-tags-${id}`);
      return savedTags ? JSON.parse(savedTags) : [];
    } catch (error) {
      console.warn('Failed to load saved tag filters:', error);
      return [];
    }
  });

  const getInitialPageSize = () => {
    const itemHeight = 70; // average item height
    const viewportHeight = window.innerHeight;
    return Math.ceil(viewportHeight / itemHeight) + 5; // +5 buffer
  };

  // Persist selected tags to localStorage whenever they change
  const handleTagFilterChange = useCallback(
    (newTags) => {
      setSelectedTags(newTags);
      try {
        if (newTags && newTags.length > 0) {
          localStorage.setItem(`transcript-filter-tags-${id}`, JSON.stringify(newTags));
        } else {
          localStorage.removeItem(`transcript-filter-tags-${id}`);
        }
      } catch (error) {
        console.warn('Failed to save tag filters:', error);
      }
    },
    [id],
  );

  // State to track if any transcripts are processing (for polling)
  const [hasProcessingTranscripts, setHasProcessingTranscripts] = useState(false);

  // Data fetching - enable polling every 5 seconds when transcripts are processing
  const {
    data: transcriptPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending: isPendingTranscripts,
  } = useFetchTranscriptList(id, selectedTags, getInitialPageSize(), {
    refetchInterval: hasProcessingTranscripts ? 5000 : false,
  });

  // Update processing state when transcript data changes
  useEffect(() => {
    if (!transcriptPages?.pages) {
      setHasProcessingTranscripts(false);
      return;
    }
    const isProcessing = transcriptPages.pages.some((page) =>
      page.transcripts?.some(
        (t) => t.status === 'processing' || t.status === 'transcribing' || t.status === 'uploading',
      ),
    );
    setHasProcessingTranscripts(isProcessing);
  }, [transcriptPages]);

  const { data: interview } = useSingleInterview(id);
  const {
    data: { studyPermission },
  } = usePermission({ study: interview });

  // Tag-related data fetching
  const { data: tagsList, isFetching: isFetchingTags } = useFetchStudyTags({
    studyId: id,
    searchQuery,
  });
  const { data: assignedTagList } = useFetchAssignedTags(id, searchQueryAssignedTag);

  // Flatten all transcript pages into a single array
  const transcripts = useMemo(() => {
    if (!transcriptPages?.pages) return [];
    return transcriptPages.pages.flatMap((page) => page.transcripts || []);
  }, [transcriptPages]);

  // Auto-select first transcript on initial load and handle deleted transcripts
  useEffect(() => {
    // Don't navigate if there are no transcripts
    if (transcripts.length === 0) {
      setInitialLoad(false);
      // If there's a transcriptId in URL but no transcripts, clear it
      if (transcriptId) {
        navigate(`/interview/${id}/transcripts`, { replace: true });
      }
      return;
    }

    if (initialLoad && transcripts.length > 0 && !transcriptId) {
      // Only auto-select if there are transcripts and no transcript is currently selected
      navigate(`/interview/${id}/transcripts/${transcripts[0].id}`, {
        replace: true,
        state: { transcript: transcripts[0] },
      });
      setInitialLoad(false);
    } else if (transcripts.length > 0) {
      // If we have transcripts but no selection, and it's not the initial load
      const transcriptExists = transcripts.some((t) => t.id === transcriptId);
      if (!transcriptExists && transcripts[0]) {
        navigate(`/interview/${id}/transcripts/${transcripts[0].id}`, {
          replace: true,
          state: { transcript: transcripts[0] },
        });
      }
      setInitialLoad(false);
    }
  }, [transcripts, transcriptId, id, initialLoad, navigate]);

  // Form setup for rename functionality
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
    },
  });

  // Custom hooks
  const { mutateAsync: deleteTranscript, isPending } = useDeleteTranscript();
  const { mutate: updateTranscriptName, isPending: isUpdatingTranscriptName } =
    useUpdateTranscriptName();
  const { mutate: addStudyTag, isPending: isAddingStudyTag } = useAddStudyTag();

  // Infinite scroll hook
  const { handleScroll } = useInfiniteScroll(fetchNextPage, hasNextPage, isFetchingNextPage);

  // Event handlers - memoized with useCallback for performance
  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setSubmenuAnchorEl(null);
  }, []);

  const handleMenuClick = useCallback((event, transcript) => {
    setAnchorEl(event.currentTarget);
    setCurrentTranscript(transcript);
  }, []);

  const handleSubmenuClose = useCallback(() => {
    setSubmenuAnchorEl(null);
  }, []);

  const handleRename = useCallback(() => {
    if (currentTranscript) {
      setTranscriptIdState(currentTranscript.id);
      setValue('name', currentTranscript.name || '');
      setOpenRenameTranscriptModal(true);
      handleMenuClose();
    }
  }, [currentTranscript, handleMenuClose, setValue]);

  const handleAssignTags = useCallback(() => {
    if (currentTranscript) {
      setTranscriptIdState(currentTranscript.id);
      setOpenTagModal(true);
      handleMenuClose();
    }
  }, [currentTranscript, handleMenuClose]);

  const handleDownloadText = useCallback(() => {
    // TODO: Implement download text functionality
    handleMenuClose();
  }, [handleMenuClose]);

  const handleDownloadCsv = useCallback(() => {
    // TODO: Implement download CSV functionality
    handleMenuClose();
  }, [handleMenuClose]);

  const handleDelete = useCallback((transcript) => {
    setCurrentTranscript(transcript);
    setOpenDeleteDialog(true);
    setAnchorEl(null);
    setSubmenuAnchorEl(null);
  }, []);

  const handleHeaderPopoverOpen = useCallback((e) => {
    setHeaderAnchorEl(e.currentTarget);
  }, []);

  const handleHeaderPopoverClose = useCallback(() => {
    setHeaderAnchorEl(null);
  }, []);

  const getTranscriptName = useCallback(
    (transcriptId) => {
      const index = transcripts?.findIndex((t) => t.id === transcriptId);
      return index !== -1 ? index + 1 : 1;
    },
    [transcripts],
  );

  const handleDeleteTextChange = useCallback((e) => {
    setDeleteTranscriptText(e.target.value);
  }, []);

  const handleConfirmDeleteTranscript = useCallback(async () => {
    const isCurrentTranscript = currentTranscript?.id === transcriptId;

    await deleteTranscript(currentTranscript?.id, {
      onSuccess: () => {
        setOpenDeleteDialog(false);
        setDeleteTranscriptText('');

        setTimeout(() => {
          if (isCurrentTranscript) {
            // Get fresh transcript list after deletion
            const updatedTranscripts = transcripts.filter((t) => t.id !== currentTranscript?.id);

            if (updatedTranscripts.length > 0) {
              const nextTranscript = updatedTranscripts[0];
              setCurrentTranscript(nextTranscript);
              navigate(`/interview/${id}/transcripts/${nextTranscript.id}`, {
                replace: true,
                state: { transcript: nextTranscript },
              });
            } else {
              setCurrentTranscript(null);
              navigate(`/interview/${id}/transcripts`, { replace: true });
            }
          }
        }, 0);
      },
    });
  }, [currentTranscript, deleteTranscript, transcriptId, transcripts, navigate, id]);

  // Rename modal handlers
  const handleCloseRenameTranscriptModal = useCallback(() => {
    setOpenRenameTranscriptModal(false);
    setTranscriptIdState(null);
    setValue('name', '');
  }, [setValue]);

  const submitForm = useCallback(
    () =>
      handleSubmit((data) => {
        updateTranscriptName(
          {
            payload: data,
            transcriptId: transcriptIdState,
            id,
          },
          {
            onSuccess: handleCloseRenameTranscriptModal,
          },
        );
      })(),
    [handleSubmit, updateTranscriptName, transcriptIdState, id, handleCloseRenameTranscriptModal],
  );

  // Tag modal handlers
  const handleCloseTagModal = useCallback(() => {
    setOpenTagModal(false);
    setTranscriptIdState(null);
  }, []);

  // Tag library modal handler
  const handleCloseTagLibraryModal = useCallback(() => {
    setOpenTagLibraryModal(false);
  }, []);

  // Get previously assigned tags for the current transcript
  const previouslyAssignedTags = useMemo(() => {
    if (!currentTranscript || !assignedTagList) return [];
    return currentTranscript.assigned_tags || [];
  }, [currentTranscript, assignedTagList]);

  const handleCompleteCancel = useCallback(() => {
    setShowCompleteConfirm(false);
  }, []);

  const handleMarkAsComplete = useCallback(() => {
    if (currentTranscript) {
      markAsCompleted(
        { studyId: id, transcriptId: currentTranscript.id, isCompleted: true },
        {
          onSuccess: () => {
            setShowCompleteConfirm(false);
            setAnchorEl(null);
            setSubmenuAnchorEl(null);
            toast.success('Transcript marked as completed successfully!');
          },
        },
      );
    }
  }, [currentTranscript, id]);

  const handleMarkAsInComplete = useCallback(() => {
    if (currentTranscript) {
      markAsCompleted(
        { studyId: id, transcriptId: currentTranscript.id, isCompleted: false },
        {
          onSuccess: () => {
            setShowCompleteConfirm(false);
            setAnchorEl(null);
            setSubmenuAnchorEl(null);
            toast.success('Transcript marked as incomplete successfully!');
          },
        },
      );
    }
  }, [currentTranscript, id]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };
  return (
    <>
      <Box className={classes.sidebarContainer}>
        <div className={classes.stickyHeader}>
          <TranscriptListHeader
            transcripts={transcripts}
            anchorEl={headerAnchorEl}
            onOpenPopover={handleHeaderPopoverOpen}
            onClose={handleHeaderPopoverClose}
            selectedTags={selectedTags}
            onTagFilterChange={handleTagFilterChange}
          />
        </div>

        <Box p={2}>
          <Button fullWidth variant="custom" fontSize="16px" onClick={handleOpen}>
            Upload your data
          </Button>
        </Box>
        {isPendingTranscripts ? (
          <Spinner />
        ) : transcripts.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '400px',
              p: 3,
            }}
          >
            <Typography variant="h6" mb={1}>
              No transcripts yet
            </Typography>
            <Typography variant="body2" color="textSecondary" textAlign="center">
              Upload your interview data to get started.
            </Typography>
          </Box>
        ) : (
          <Box className={classes.fieldList} onScroll={handleScroll}>
            {transcripts?.map((transcript, index) => {
              return (
                <TranscriptItem
                  key={transcript.id}
                  transcript={transcript}
                  index={index}
                  isActive={transcriptId === transcript.id}
                  getTranscriptName={getTranscriptName}
                  assignedTagsCount={transcript?.assigned_tags?.length || 0}
                  assignedTags={transcript?.assigned_tags}
                  onMenuClick={handleMenuClick}
                />
              );
            })}

            {/* Loading indicator for infinite scroll */}
            {isFetchingNextPage && (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Box>
        )}

        {/* Transcript Menu */}
        <Menu
          id="transcript-menu"
          open={Boolean(anchorEl)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          anchorEl={anchorEl}
          onClose={handleMenuClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          {studyPermission.edit && (
            <MenuItem onClick={handleRename}>
              <ListItemText>Rename Transcript</ListItemText>
            </MenuItem>
          )}
          {studyPermission.edit && (
            <MenuItem onClick={handleAssignTags}>
              <ListItemText>Assign Tags</ListItemText>
            </MenuItem>
          )}
          {studyPermission.edit && currentTranscript?.is_complete === false && (
            <MenuItem onClick={() => setShowCompleteConfirm(true)}>
              <ListItemText>Mark as Complete</ListItemText>
            </MenuItem>
          )}
          {studyPermission.edit && currentTranscript?.is_complete === true && (
            <MenuItem onClick={() => setShowCompleteConfirm(true)}>
              <ListItemText>Mark as Incomplete</ListItemText>
            </MenuItem>
          )}
          {/* <MenuItem onClick={handleSubmenuClick}>
          <ListItemText>Download </ListItemText>
          <ArrowRightIcon />
        </MenuItem> */}
          {studyPermission.delete && (
            <MenuItem onClick={() => handleDelete(currentTranscript)} sx={{ color: 'error.main' }}>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          )}
        </Menu>

        {/* Download Submenu */}
        <Menu
          anchorEl={submenuAnchorEl}
          open={Boolean(submenuAnchorEl)}
          onClose={handleSubmenuClose}
          // anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          // transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          MenuListProps={{
            onMouseEnter: () => setSubmenuAnchorEl(submenuAnchorEl),
            onMouseLeave: handleSubmenuClose,
          }}
        >
          <MenuItem onClick={handleDownloadText}>Text</MenuItem>
          <MenuItem onClick={handleDownloadCsv}>CSV</MenuItem>
        </Menu>

        {/* Delete transcript */}
        {openDeleteDialog && (
          <DeleteConfirmationDialog
            open={openDeleteDialog}
            onClose={() => {
              setOpenDeleteDialog(false);
              setCurrentTranscript(null);
              setDeleteTranscriptText('');
            }}
            onConfirm={handleConfirmDeleteTranscript}
            isPending={isPending}
            title="Delete Transcription"
            okText={isPending ? 'Deleting...' : 'Delete'}
            disabled={deleteTranscriptText?.trim() !== currentTranscript?.name}
            content={
              <Box>
                <Typography>
                  This action cannot be undone. Are you sure you want to delete{' '}
                  <b>{currentTranscript?.name}</b>?
                </Typography>
                <Typography sx={{ mt: 1, color: 'text.secondary', fontSize: '0.875rem' }}>
                  <b>Note:</b> This only deletes the transcript. If you have run any analysis on
                  this study, quotes from this transcript may still appear in those analysis
                  results. To completely remove all references, please also delete any relevant
                  analysis items from the &quot;Analysis Results&quot; tab.
                </Typography>
                <Box mt={2}>
                  <Typography mb={0.5}>
                    To confirm, kindly enter the transcript name below.
                  </Typography>
                  <TextField
                    fullWidth
                    sx={{ mt: 0.4 }}
                    placeholder={currentTranscript?.name}
                    value={deleteTranscriptText}
                    onChange={handleDeleteTextChange}
                  />
                </Box>
              </Box>
            }
          />
        )}

        {openRenameTranscriptModal && (
          <RenameTranscriptModal
            open={openRenameTranscriptModal}
            onClose={handleCloseRenameTranscriptModal}
            onConfirm={submitForm}
            isPending={isUpdatingTranscriptName}
            transcriptId={transcriptIdState}
            register={register}
            errors={errors}
            editTranscriptNameRef={editTranscriptNameRef}
          />
        )}

        {openTagModal && (
          <AssignTags
            id={id}
            onClose={handleCloseTagModal}
            open={openTagModal}
            tags={tagsList}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            transcriptId={transcriptIdState}
            isFetchingTags={isFetchingTags}
            addStudyTag={addStudyTag}
            isAddingStudyTag={isAddingStudyTag}
            canEdit={studyPermission.edit}
            assigned_tags={previouslyAssignedTags}
          />
        )}

        {openTagLibraryModal && (
          <TagLibraryModal
            open={openTagLibraryModal}
            onClose={handleCloseTagLibraryModal}
            id={id}
            tagsList={tagsList}
            transcriptId={transcriptIdState}
            addStudyTag={addStudyTag}
            isAddingStudyTag={isAddingStudyTag}
            isFetchingTags={isFetchingTags}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
          />
        )}
      </Box>
      <ConfirmationDialog
        open={showCompleteConfirm}
        onClose={handleCompleteCancel}
        onConfirm={currentTranscript?.is_complete ? handleMarkAsInComplete : handleMarkAsComplete}
        isPending={isMarkingAsCompletedPending}
        title={currentTranscript?.name}
        okText={'Confirm'}
        noText={'Cancel'}
        content={
          <Box>
            <Typography>
              Do you want to mark <strong>{currentTranscript?.name}</strong> as{' '}
              {currentTranscript?.is_complete ? 'incomplete' : 'completed'} ?
            </Typography>
          </Box>
        }
      />

      <Modal
        open={open}
        // onClose={handleClose}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: '556px',
            // height: {
            //   xs: '85vh',
            //   sm: '80vh',
            // },
            // backgroundColor: 'white',
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <FileUpload onClose={handleClose} />
        </Box>
      </Modal>
    </>
  );
};

export default TranscriptList;
